import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeftOutlined,
  CaretDownOutlined,
  SbButton,
  SbTabs,
  SbTable,
  SbDropdown,
  SbSpin,
  SbCard,
  SbTooltip,
  SbInfoTooltip,
  SbEmpty,
  TooltipTriggerStyled,
} from "uiCore";
import useAuthStore from "Store/useAuthStore";
import usePAICStore from "Store/usePAICStore";
import { SbNotification } from "uiCore";
import { getPAICRoleImpact } from "Api/apiService";
import Permission from "Components/Permission";
import analytics from "Utils/analytics";
import { handlePAICErrorCode, PAIC_ERROR_MESSAGES } from "constants/peoplePlusAI.constants";
import { PAIC_ROUTES } from "constants/paicRoutes.constants";
import { Typography } from "antd";
import {
  GROUP_DETAIL_TABS,
  PEOPLE_PLUS_AI_MENU_ITEMS,
} from "constants/groupAgents.constants";

import {
  PageContainer,
  HeaderSection,
  HeaderTopRow,
  BackButton,
  TabsRow,
  TabLabelWithCaret,
  ContentSection,
  RoleImpactGrid,
  SectionTitle,
  SectionSubTitle,
  RoleDescription,
  RoleCharterText,
  ScrollableContent,
  DetailBlock,
  DetailLabel,
  DetailText,
  WorkflowList,
  OverallStats,
  OverallStatsTitle,
  StatRow,
  StatLabel,
  ProgressBarTrack,
  ProgressBarPeople,
  ProgressBarAI,
  MetricsList,
  MetricCard,
  MetricTextGroup,
  MetricTitle,
  MetricFormula,
  MetricValue,
  ViewMoreButtonRow,
  ComparisonHeader,
  ComparisonTableWrap,
  ComingSoonText,
  LoadingContainer,
  EmptyStateWrap,
} from "./SbPACRoleImpactV2.styles";

const { Title, Text } = Typography;

// PRD §5.13 — tooltip content strings for Role Impact
const TOOLTIP_BACK = "Return to the Group Agents screen.";
const TOOLTIP_TASK_NAME_HEADER = "Click to sort tasks alphabetically by name.";
const TOOLTIP_AI_POTENTIAL_HEADER =
  "Click to sort tasks by AI automation potential — highest or lowest first.";
const TOOLTIP_OVERALL_AI_POTENTIAL =
  "The percentage of this role's total work that could be augmented or automated by the selected AI agents.";
const TOOLTIP_PEOPLE_WORKLOAD =
  "The percentage of work that will remain with the human occupying this role after AI deployment.";
const TOOLTIP_AI_WORKLOAD =
  "The percentage of work that will be handled by AI agents after deployment.";

const formatPercentage = (value) => {
  const num = Number(value);
  if (Number.isNaN(num)) return "0%";
  return `${Math.round(num)}%`;
};

const formatCurrency = (value) => {
  const num = Number(value);
  if (Number.isNaN(num)) return "$0";
  return `$${Math.round(num).toLocaleString()}`;
};

const SbPACRoleImpactV2 = () => {
  const navigate = useNavigate();

  const email = useAuthStore((s) => s.email);
  const selectionId = usePAICStore((s) => s.selectionId);
  const roleSBUniqueId = usePAICStore((s) => s.roleSBUniqueId);
  const loading = usePAICStore((s) => s.loading);
  const setLoading = usePAICStore((s) => s.setLoading);
  const rolePositionCount = usePAICStore((s) => s.rolePositionCount);
  const roleTotalComp = usePAICStore((s) => s.roleTotalComp);

  const [activeTab, setActiveTab] = useState("people-plus-ai");
  const [roleImpactData, setRoleImpactData] = useState(null);

  const fetchRoleImpact = async () => {
    if (!selectionId || !roleSBUniqueId) return;

    setLoading(true);
    try {
      const response = await getPAICRoleImpact({
        selection_id: selectionId,
        sb_unique_id: roleSBUniqueId,
      });

      if (response && response.length > 0) {
        setRoleImpactData(response[0]);
      }
    } catch (error) {
      if (!handlePAICErrorCode(error, SbNotification.error)) {
        SbNotification.error(PAIC_ERROR_MESSAGES.LOAD_ROLE_IMPACT_FAILED);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoleImpact();
  }, [selectionId, roleSBUniqueId]);

  useEffect(() => {
    if (!roleImpactData) return;

    analytics.trackPAICRoleImpactViewed({
      from: "pac_role_impact",
      email,
      roleId: roleSBUniqueId,
      roleName: roleImpactData.job_title,
      description: `Viewed role impact for: ${roleImpactData.job_title}`,
    });
  }, [roleImpactData, email, roleSBUniqueId]);

  const handleOnBackClick = () => {
    analytics.trackPAICCloseRoleImpact({
      from: "pac_role_impact",
      email,
      description: "Closed role impact view",
    });

    const setViewByToggle = usePAICStore.getState().setViewByToggle;
    setViewByToggle(true);

    navigate(PAIC_ROUTES.GROUP_AGENTS, { state: { fromParent: true } });
  };

  const handlePeopleAIMenuClick = ({ key }) => {
    setActiveTab("people-plus-ai");
    if (key === "for-role") return;
  };

  const handleViewMoreClick = () => {
    analytics.trackPAICViewMoreRoleImpact({
      from: "pac_role_impact",
      email,
      roleId: roleSBUniqueId,
      roleName: roleImpactData?.job_title,
      description: "Navigating to detailed role impact view",
    });

    navigate(PAIC_ROUTES.ROLE_IMPACT_DETAILS, {
      state: { fromParent: true },
    });
  };

  // TODO: Dropdown has no functionality - commented out until implemented
  // const peopleAIMenuItems = PEOPLE_PLUS_AI_MENU_ITEMS.map((item) => ({
  //   key: item.key,
  //   label: item.label,
  // }));

  const tabItems = GROUP_DETAIL_TABS.map((tab) => {
    // TODO: Dropdown removed temporarily - no functionality implemented
    // if (tab.key === "people-plus-ai") {
    //   return {
    //     key: tab.key,
    //     label: (
    //       <SbDropdown
    //         trigger={["click"]}
    //         menu={{
    //           items: peopleAIMenuItems,
    //           onClick: handlePeopleAIMenuClick,
    //         }}
    //       >
    //         <TabLabelWithCaret onClick={(e) => e.stopPropagation()}>
    //           {tab.label}
    //           <CaretDownOutlined />
    //         </TabLabelWithCaret>
    //       </SbDropdown>
    //     ),
    //   };
    // }

    return {
      key: tab.key,
      label: tab.label,
    };
  });

  const comparisonColumns = useMemo(
    () => [
      {
        title: "#",
        dataIndex: "index",
        key: "index",
        width: 52,
        align: "center",
        render: (value) => <strong>{value}.</strong>,
      },
      {
        title: (
          <SbTooltip content={TOOLTIP_TASK_NAME_HEADER}>
            <span>Task</span>
          </SbTooltip>
        ),
        dataIndex: "task",
        key: "task",
        width: "35%",
        ellipsis: false,
        render: (value) => value || "-",
      },
      {
        title: (
          <SbTooltip content={TOOLTIP_AI_POTENTIAL_HEADER}>
            <span>Without AI Agents</span>
          </SbTooltip>
        ),
        dataIndex: "withoutAgents",
        key: "withoutAgents",
        width: "32.5%",
        ellipsis: false,
        render: (value) => value || "-",
      },
      {
        title: (
          <SbTooltip content={TOOLTIP_AI_POTENTIAL_HEADER}>
            <span>With AI Agents</span>
          </SbTooltip>
        ),
        dataIndex: "withAgents",
        key: "withAgents",
        width: "32.5%",
        ellipsis: false,
        render: (value) => value || "-",
      },
    ],
    [],
  );

  const comparisonData = useMemo(() => {
    return (roleImpactData?.parent_tasks || []).map((task, index) => {
      const impact = roleImpactData?.role_impact?.[index]?.role_impact || {};

      return {
        key: `${task?.parent_task || "task"}-${index}`,
        index: index + 1,
        task: task?.parent_task || "N/A",
        withoutAgents: impact?.without_agents || "N/A",
        withAgents: impact?.with_agents_focus || "N/A",
      };
    });
  }, [roleImpactData]);

  if (loading || !roleImpactData) {
    return (
      <PageContainer>
        <LoadingContainer>
          <SbSpin size="large" />
        </LoadingContainer>
      </PageContainer>
    );
  }

  const {
    job_title,
    role_impact_summary,
    ai_percentage_c,
    total_positions,
    hours_per_role,
    potential_impact_hours_c,
    total_role_saving_c,
  } = roleImpactData;

  const peoplePercent = Math.max(0, 100 - Number(ai_percentage_c || 0));
  const aiPercent = Math.max(0, Number(ai_percentage_c || 0));

  const renderComingSoon = (label) => (
    <ComingSoonText>{label} is coming soon.</ComingSoonText>
  );

  return (
    <PageContainer>
      <HeaderSection>
        <HeaderTopRow>
          <SbTooltip content={TOOLTIP_BACK}>
            <TooltipTriggerStyled>
              <BackButton onClick={handleOnBackClick} type="button">
                <ArrowLeftOutlined />
              </BackButton>
            </TooltipTriggerStyled>
          </SbTooltip>
          <Title level={3}>Job Details: {job_title}</Title>
        </HeaderTopRow>

        
          <TabsRow>
            <SbTabs
              activeKey={activeTab}
              onChange={setActiveTab}
              items={tabItems}
            />
          </TabsRow>
        
      </HeaderSection>

      <ContentSection>
        {activeTab === "people-plus-ai" && (
          <RoleImpactGrid>
            <SbCard bordered>
              <SectionTitle>
                People+AI Collaboration Potential for Selected Role
              </SectionTitle>

              <SectionSubTitle>
                A clearer view of the current role, transformation, workflow,
                and collaboration potential.
              </SectionSubTitle>

              <RoleDescription>
                <RoleCharterText>
                  <strong>Role Charter:</strong>{" "}
                  {role_impact_summary?.role_charter || "N/A"}
                </RoleCharterText>
              </RoleDescription>

              <ScrollableContent>
                <DetailBlock>
                  <DetailLabel>Before Role</DetailLabel>
                  <DetailText>{role_impact_summary?.before_role || "N/A"}</DetailText>
                </DetailBlock>

                <DetailBlock>
                  <DetailLabel>After Role</DetailLabel>
                  <DetailText>{role_impact_summary?.after_role || "N/A"}</DetailText>
                </DetailBlock>

                <DetailBlock $last>
                  <DetailLabel>Workflow</DetailLabel>
                  {role_impact_summary?.bullets?.length ? (
                    <WorkflowList>
                      {role_impact_summary.bullets.map((bullet, index) => (
                        <li key={index}>{bullet}</li>
                      ))}
                    </WorkflowList>
                  ) : (
                    <DetailText>No workflow data available</DetailText>
                  )}
                </DetailBlock>
              </ScrollableContent>

              <OverallStats>
                <OverallStatsTitle>
                  Overall People+AI Collaboration Potential for Selected Role
                </OverallStatsTitle>

                <StatRow>
                  <StatLabel>Task break-up</StatLabel>
                  <ProgressBarTrack>
                    <SbTooltip content={TOOLTIP_PEOPLE_WORKLOAD}>
                      <ProgressBarPeople $width={peoplePercent}>
                        {formatPercentage(peoplePercent)} People
                      </ProgressBarPeople>
                    </SbTooltip>
                    <SbTooltip content={TOOLTIP_AI_WORKLOAD}>
                      <ProgressBarAI $width={aiPercent}>
                        {formatPercentage(aiPercent)} AI
                      </ProgressBarAI>
                    </SbTooltip>
                  </ProgressBarTrack>
                </StatRow>

                <MetricsList>
                  <Permission resource="people_plus_ai" actions={["view_comp"]}>
                    <MetricCard>
                      <MetricTextGroup>
                        <MetricTitle>
                          <SbInfoTooltip content={TOOLTIP_OVERALL_AI_POTENTIAL}>
                            Potential value created by AI
                          </SbInfoTooltip>
                        </MetricTitle>
                        <MetricFormula>
                          (% AI x No. of Positions x Average compensation)
                        </MetricFormula>
                      </MetricTextGroup>

                      <MetricValue>
                        {formatCurrency(total_role_saving_c)}
                      </MetricValue>
                    </MetricCard>
                  </Permission>

                  <Permission resource="people_plus_ai" actions={["view_hours"]}>
                    <MetricCard>
                      <MetricTextGroup>
                        <MetricTitle>
                          <SbInfoTooltip content={TOOLTIP_AI_WORKLOAD}>
                            Potential hours saved by AI
                          </SbInfoTooltip>
                        </MetricTitle>
                        <MetricFormula>
                          (% AI x No. of Positions x Hours per role)
                        </MetricFormula>
                      </MetricTextGroup>

                      <SbTooltip
                        content={`${formatPercentage(aiPercent)} × ${
                          total_positions || 2
                        } × ${Math.round(hours_per_role || 0)}`}
                      >
                        <MetricValue>
                          {Math.round(potential_impact_hours_c || 0)}
                        </MetricValue>
                      </SbTooltip>
                    </MetricCard>
                  </Permission>
                </MetricsList>

                <ViewMoreButtonRow>
                  <SbButton
                    buttonType="primary"
                    size="large"
                    onClick={handleViewMoreClick}
                  >
                    View More
                  </SbButton>
                </ViewMoreButtonRow>
              </OverallStats>
            </SbCard>

            <SbCard bordered>
              <ComparisonHeader>
                <SectionTitle>Role Comparison</SectionTitle>
                <SectionSubTitle>
                  Compare work with and without AI agents.
                </SectionSubTitle>
              </ComparisonHeader>

              <ComparisonTableWrap>
                {comparisonData.length > 0 ? (
                  <SbTable
                    columns={comparisonColumns}
                    dataSource={comparisonData}
                    pagination={false}
                    rowKey="key"
                    tableLayout="fixed"
                    size="large"
                  />
                ) : (
                  <EmptyStateWrap>
                    <SbEmpty description="No task data available" />
                  </EmptyStateWrap>
                )}
              </ComparisonTableWrap>
            </SbCard>
          </RoleImpactGrid>
        )}

        {activeTab === "learning-catalyst" &&
          renderComingSoon("Learning Catalyst")}
        {activeTab === "talent-mobility" && renderComingSoon("Talent Mobility")}
        {activeTab === "jd" && renderComingSoon("Jobs Library")}
        {activeTab === "skills-bridger" && renderComingSoon("Skills Bridger")}
      </ContentSection>
    </PageContainer>
  );
};

export default SbPACRoleImpactV2;